import { ArrowUpRight, Shield, TrendingUp, Crown } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden"
    >
      {/* Ambient radial glow */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[60vw] h-[60vw] ambient-radial pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-0 w-[55vw] h-[55vw] ambient-radial pointer-events-none opacity-70"
      />
      {/* subtle grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",
          backgroundSize: "120px 120px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-12 items-center w-full">
        {/* LEFT */}
        <div className="lg:col-span-6 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-[11px] uppercase tracking-[0.28em] text-gray-300 mb-10">
            <span className="h-1.5 w-1.5 rounded-full bg-[#0a8a5e] shadow-[0_0_10px_#0a8a5e]" />
            Modern Wealth & Protection
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif font-light text-white leading-[1.02] tracking-tight text-balance"
          >
            <span className="block text-5xl sm:text-6xl lg:text-[5.5rem]">
              Protect Your Future.
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-[5.5rem]">
              Build Wealth.
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-[5.5rem] italic text-rolex-light">
              Create Opportunity.
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-10 max-w-xl text-lg text-gray-400 leading-relaxed font-light"
          >
            Modern life insurance and wealth strategies designed for families,
            entrepreneurs, and future agents.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-policy"
              onClick={() => scrollTo("#calendly-embed")}
              className="group inline-flex items-center justify-center gap-2 bg-[#006241] hover:bg-[#0a8a5e] text-white px-8 py-4 rounded-full text-[15px] font-medium transition-all duration-300 shadow-[0_0_40px_rgba(0,98,65,0.35)] hover:shadow-[0_0_60px_rgba(0,98,65,0.55)]"
            >
              Get a Policy Strategy
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-agent"
              onClick={() => scrollTo("#careers")}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/15 hover:border-white/40 text-white hover:bg-white/[0.04] px-8 py-4 rounded-full text-[15px] font-medium transition-all duration-300"
            >
              Become an Agent
            </button>
          </div>

          {/* Feature icons */}
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl">
            {[
              { icon: Shield, label: "Protect Your Family" },
              { icon: TrendingUp, label: "Build Wealth" },
              { icon: Crown, label: "Create Legacy" },
            ].map((f, i) => (
              <div
                key={f.label}
                data-testid={`hero-feature-${i}`}
                className="flex items-center gap-3"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-full border border-rolex/40 bg-rolex/[0.08]">
                  <f.icon className="h-4 w-4 text-rolex-light" />
                </span>
                <span className="text-sm text-gray-300 tracking-wide">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-12 ambient-radial blur-3xl pointer-events-none"
            />
            <div className="relative">
              <img
                src={SITE.images.heroPortrait}
                alt="Semira Emeasoba"
                className="w-full h-[70vh] lg:h-[100vh] object-contain"
                loading="eager"
                data-testid="hero-portrait"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(ellipse 55% 60% at 50% 42%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 55% 60% at 50% 42%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0) 100%)",
                }}
              />
              <div className="mt-6 flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-gray-500">
                    Founder & Licensed Strategist
                  </div>
                  <div className="font-serif text-3xl text-white mt-2">
                    Semira Emeasoba
                  </div>
                </div>
                <span className="h-2 w-2 rounded-full bg-rolex-light shadow-[0_0_12px_#0a8a5e] float-slow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
